import { sortLegsByDate } from 'utils/sortLegsByDate';
import { types } from 'types/passengers';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { BLOCKED_SEATS_TYPE } from 'constants/addPassengers';
import { EVAC_ASSIST_TARIFF_CODE } from 'constants/tariffCodes';

export const EVAC_PASSENGER_TYPE = 'EVACUATION_CHAIR';

export const getNonGroupPassengerTypeOptions = ({
  bookingHasAnimals,
  hasSnapTickets,
  hasWheelchairPassenger,
  hasWheelchairCompanion,
  hasNonWheelchairCompanion,
  isContinentalBooking,
  isLoyaltyBooking,
  isDutyTollFree,
  isTravelAgent,
  isEvacuationChairEnabled
}) => {
  const hasHandicappedPassenger = hasWheelchairPassenger || hasWheelchairCompanion || hasNonWheelchairCompanion;
  const canShowGeneral = !hasSnapTickets && !hasHandicappedPassenger && !isLoyaltyBooking && !isDutyTollFree;
  const canShowSpecialAssistance = !isLoyaltyBooking && !isDutyTollFree && !isTravelAgent;
  const canShowAnimals = !bookingHasAnimals && !isTravelAgent;
  const isAnimalsDisabled = isContinentalBooking;
  const canShowBlockedSeats = !hasSnapTickets && !hasHandicappedPassenger && !isLoyaltyBooking && !isDutyTollFree && !isTravelAgent;
  const canShowEvacuationChair = canShowBlockedSeats && isEvacuationChairEnabled;

  return {
    canShowGeneral,
    canShowSpecialAssistance,
    canShowAnimals,
    isAnimalsDisabled,
    shouldShowAnimalsAlreadyBooked: bookingHasAnimals,
    canShowBlockedSeats,
    canShowEvacuationChair,
    hasAvailableOptions:
      canShowGeneral || canShowSpecialAssistance || (canShowAnimals && !isAnimalsDisabled) || canShowBlockedSeats || canShowEvacuationChair
  };
};

export const getGroupPassengerTypeOptions = ({
  bookingHasAnimals,
  isContinentalBooking,
  isTravelAgent,
  hasWheelchairPassenger,
  hasWheelchairCompanion,
  hasNonWheelchairCompanion,
  isLoyaltyBooking,
  isDutyTollFree,
  hasSnapTickets
}) => {
  const canShowGeneral = true;
  const hasHandicappedPassenger = hasWheelchairPassenger || hasWheelchairCompanion || hasNonWheelchairCompanion;
  const canShowSpecialAssistance = !isTravelAgent;
  const canShowAnimals = !bookingHasAnimals && !isTravelAgent;
  const isAnimalsDisabled = isContinentalBooking;
  const canShowBlockedSeats = !hasSnapTickets && !hasHandicappedPassenger && !isLoyaltyBooking && !isDutyTollFree && !isTravelAgent;

  return {
    canShowGeneral,
    canShowSpecialAssistance,
    canShowAnimals,
    isAnimalsDisabled,
    shouldShowAnimalsAlreadyBooked: bookingHasAnimals,
    canShowBlockedSeats,
    hasAvailableOptions: canShowGeneral || canShowSpecialAssistance || (canShowAnimals && !isAnimalsDisabled) || canShowBlockedSeats
  };
};

export const findEarliest = (legs) => {
  if (legs && legs.length <= 0) {
    return null;
  }

  const [{ timing }] = [...legs].sort(sortLegsByDate);

  return timing.date;
};

export const findEarliestLeg = (legs) => {
  if (legs && legs.length <= 0) {
    return null;
  }

  return [...legs].sort(sortLegsByDate)[0];
};

const buildPassengersOfType = (type, disabilityType, count = 0, startIndex = 0) =>
  Array(count)
    .fill({ type, disabilityType })
    .map((value, index) => ({
      id: `passenger_${index + startIndex}`,
      ...value
    }));

export const buildPassengers = ({
  booking,
  shouldBuildChildrenOnlyPassengers = false,
  adults,
  children,
  youths,
  seniors,
  wheelchairCompanions,
  nonWheelchairCompanions,
  wheelchairAdults,
  wheelchairChild,
  guideDogs = 0,
  adultsEvac = 0,
  childrenEvac = 0
}) => {
  const index = booking.passengers.length + 1;

  if (shouldBuildChildrenOnlyPassengers) {
    return [
      ...buildPassengersOfType('CH', types.NO_HANDICAP, children, index),
      ...buildPassengersOfType('AD', types.NO_HANDICAP, 1, index + children)
    ];
  }

  const isGroupBooking = booking.isGroupBooking;
  const paxOrder = isGroupBooking
    ? buildPassengersOfType('YTH', types.NO_HANDICAP, youths, index)
        .concat(buildPassengersOfType('AD', types.NO_HANDICAP, adults, index + youths))
        .concat(buildPassengersOfType('SNR', types.NO_HANDICAP, seniors, index + youths + adults))
        .concat(buildPassengersOfType('CH', types.NO_HANDICAP, children, index + youths + adults + seniors))
    : buildPassengersOfType('AD', types.NO_HANDICAP, adults, index)
        .concat(buildPassengersOfType('CH', types.NO_HANDICAP, children, index + adults))
        .concat(buildPassengersOfType('YTH', types.NO_HANDICAP, youths, index + adults + children))
        .concat(buildPassengersOfType('SNR', types.NO_HANDICAP, seniors, index + adults + children + youths));

  const passengers = paxOrder
    .concat(buildPassengersOfType('AD', types.COMPANION, wheelchairCompanions, index + adults + children + youths + seniors))
    .concat(
      buildPassengersOfType(
        'AD',
        types.NO_WHEELCHAIR_COMPANION,
        nonWheelchairCompanions,
        index + adults + children + youths + seniors + wheelchairCompanions
      )
    )
    .concat(
      buildPassengersOfType(
        'AD',
        types.WHEELCHAIR,
        wheelchairAdults,
        index + adults + children + youths + seniors + wheelchairCompanions + nonWheelchairCompanions
      )
    )
    .concat(
      buildPassengersOfType(
        'CH',
        types.WHEELCHAIR,
        wheelchairChild,
        index + adults + children + youths + seniors + wheelchairCompanions + nonWheelchairCompanions + wheelchairAdults
      )
    )
    .concat(
      buildPassengersOfType(
        'DOG',
        types.NO_WHEELCHAIR_COMPANION,
        guideDogs,
        index + adults + children + youths + seniors + wheelchairCompanions + nonWheelchairCompanions + wheelchairAdults + wheelchairChild
      )
    )
    .concat(
      buildPassengersOfType(
        'AD',
        types.EVAC,
        adultsEvac,
        index +
          adults +
          children +
          youths +
          seniors +
          wheelchairCompanions +
          nonWheelchairCompanions +
          wheelchairAdults +
          wheelchairChild +
          guideDogs
      )
    )
    .concat(
      buildPassengersOfType(
        'CH',
        types.EVAC,
        childrenEvac,
        index +
          adults +
          children +
          youths +
          seniors +
          wheelchairCompanions +
          nonWheelchairCompanions +
          wheelchairAdults +
          wheelchairChild +
          guideDogs +
          adultsEvac
      )
    );

  return passengers;
};

export const getFaresByDirection = (direction, searchDirection, isChildrenOnly = false, newPassengerIds = []) => {
  if (!direction) {
    return [];
  }

  const journey = searchDirection?.journeys?.find(
    (jrn) =>
      jrn.fares?.filter((fare) =>
        fare.legs?.find(
          (leg) =>
            leg.trainNumber === direction.trainNumber &&
            leg.destination.uic === direction.destination.uic &&
            leg.origin.uic === direction.origin.uic
        )
      ).length > 0
  );

  const filteredJourneyFares = journey?.fares?.filter((fr) => fr.bookingItems.length > 0 && fr.canBeBooked) ?? [];

  if (isChildrenOnly) {
    return filteredJourneyFares.map((fare) => ({
      ...fare,
      bookingItems: fare.bookingItems.filter(({ passengerId }) => newPassengerIds.includes(passengerId))
    }));
  }

  return filteredJourneyFares;
};

export const buildSegmentItems = ({ bookingItems = [], passengersType }) => {
  const items = bookingItems.map(({ passengerId, tariffCode }) => ({
    passengerId,
    tariffCode
  }));

  // Only 1 Evac can be added and unavailable for indirect so
  // the needed pax id is always the first item
  if (passengersType === EVAC_PASSENGER_TYPE) {
    items.push({ passengerId: bookingItems[0].passengerId, tariffCode: EVAC_ASSIST_TARIFF_CODE });
  }

  return items;
};

export const hasChildrenAlone = ({ values = {} }) => {
  const passengersByType = {
    adults: Number(values.adults || 0),
    children: Number(values.children || 0),
    youths: Number(values.youths || 0),
    seniors: Number(values.seniors || 0)
  };

  return (
    passengersByType.children > 0 &&
    [passengersByType.adults, passengersByType.youths, passengersByType.seniors].every((paxNumber) => paxNumber === 0)
  );
};

export const productFamiliesToSearch = ({ passengersByType, searchForPub = false, booking = {} }) => {
  if (passengersByType.blockedSeats > 0) return [BLOCKED_SEATS_TYPE];
  if (searchForPub) return [productFamilyCodes.PUB];
  return [booking.productFamilyGroup];
};

export const getSelectedProductFamilyLabels = ({ booking = {}, selectedItemRefs = [] }) => {
  const { outbound = [], inbound = [], productFamily, productFamilyGroup } = booking;

  const getLabel = (legs) => {
    const selectedProduct = legs
      .filter(({ cancelled }) => !cancelled)
      .flatMap(({ products = [] }) => products.filter(({ cancelled, itemRef }) => !cancelled && selectedItemRefs.includes(itemRef)))[0];

    return (
      selectedProduct?.productFamily?.familyGroupName ??
      selectedProduct?.productFamily?.familyGroup ??
      productFamily ??
      productFamilyGroup ??
      ''
    );
  };

  const hasOutboundSelection = outbound.some(
    ({ cancelled, products = [] }) => !cancelled && products.some(({ cancelled: c, itemRef }) => !c && selectedItemRefs.includes(itemRef))
  );
  const hasInboundSelection = inbound.some(
    ({ cancelled, products = [] }) => !cancelled && products.some(({ cancelled: c, itemRef }) => !c && selectedItemRefs.includes(itemRef))
  );

  const outboundLabel = hasOutboundSelection ? getLabel(outbound) : '';
  const inboundLabel = hasInboundSelection ? getLabel(inbound) : '';

  return { outboundLabel, inboundLabel };
};
